import type { DashboardLayout } from '@sea-snag/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useStore } from '@tanstack/react-store';
import { useCallback, useEffect, useRef } from 'react';

import { DEFAULT_LAYOUT, WIDGET_REGISTRY } from '../components/dashboard/widget-registry';
import { useOrganization } from '../contexts/organization-context';
import { dashboardApi } from '../lib/api-client';
import { queryKeys, STALE_TIMES } from '../lib/query-keys';
import {
  closeCatalog,
  dashboardStore,
  markSaved,
  openCatalog,
  setLayout,
  toggleEditMode as toggleEditModeAction,
} from '../stores/dashboard-store';

// ── Hook ─────────────────────────────────────────────────────────────

interface UseDashboardReturn {
  layout: DashboardLayout;
  isLoading: boolean;
  isEditMode: boolean;
  isCatalogOpen: boolean;
  isSaving: boolean;
  toggleEditMode: () => void;
  openCatalog: () => void;
  closeCatalog: () => void;
}

export function useDashboard(): UseDashboardReturn {
  const { organization } = useOrganization();
  const orgPid = organization?.pid ?? '';
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const layout = useStore(dashboardStore, (s) => s.layout);
  const isEditMode = useStore(dashboardStore, (s) => s.isEditMode);
  const isCatalogOpen = useStore(dashboardStore, (s) => s.isCatalogOpen);
  const isDirty = useStore(dashboardStore, (s) => s.isDirty);
  const revision = useStore(dashboardStore, (s) => s.revision);

  // ── Fetch layout ───────────────────────────────────────────────────

  const { data, isLoading: isQueryLoading } = useQuery({
    queryKey: queryKeys.dashboard.layout(orgPid),
    queryFn: () => dashboardApi.getLayout(),
    enabled: !!orgPid,
    staleTime: STALE_TIMES.LONG,
  });

  const isLoading = isQueryLoading || !orgPid;

  // When the org switches, clear the previous org's layout immediately so a
  // superadmin doesn't briefly see the prior firm's widgets while the next
  // query is in flight.
  useEffect(() => {
    setLayout({ widgets: [] });
  }, [orgPid]);

  // Sync fetched layout into store. `data === null` means the API explicitly
  // says no saved layout for this user, in which case we hydrate the default.
  useEffect(() => {
    if (data) {
      const widgets = data.widgets.filter((w) => Object.hasOwn(WIDGET_REGISTRY, w.type));
      setLayout({ ...data, widgets });
    } else if (data === null && !isQueryLoading) {
      const widgets = DEFAULT_LAYOUT.widgets.map((w, i) => ({
        ...w,
        id: crypto.randomUUID(),
        position: i,
      }));
      setLayout({ widgets });
    }
  }, [data, isLoading]);

  // ── Save mutation ──────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (layoutToSave: DashboardLayout) => dashboardApi.updateLayout(layoutToSave),
    onSuccess: () => {
      markSaved();
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.layout(orgPid) });
    },
  });

  // ── Debounced save on layout changes ────────────────────────────────

  useEffect(() => {
    if (!isDirty || !orgPid || revision === 0) return;

    debounceTimerRef.current = setTimeout(() => {
      saveMutation.mutate(dashboardStore.state.layout);
    }, 1000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [revision, orgPid]);

  // ── Toggle edit mode with immediate save ───────────────────────────

  const handleToggleEditMode = useCallback(() => {
    const wasEditMode = dashboardStore.state.isEditMode;
    toggleEditModeAction();

    // Immediate save when exiting edit mode
    if (wasEditMode && dashboardStore.state.isDirty && orgPid) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      saveMutation.mutate(dashboardStore.state.layout);
    }
  }, [orgPid]);

  return {
    layout,
    isLoading,
    isEditMode,
    isCatalogOpen,
    isSaving: saveMutation.isPending,
    toggleEditMode: handleToggleEditMode,
    openCatalog,
    closeCatalog,
  };
}
